import {
  Box,
  Grid,
  Stack,
  Typography,
  useTheme
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  CarouselCardGrid,
  CarouselSlideBox,
  StyledStatusBox,
  StyledSwipeableDrawer
} from "./CarouselCardMobile.styles";
import type {
  CarouselCardMobileProps,
  StatusBoxProps
} from "./CarouselCardMobile.types";
import {
  CardCategories,
  CardCta,
  CardSignature,
  CardType,
  Icon,
  LinkWithInteractableOverlay
} from "../../components";
import { LEVEL, STATUS_LO } from "../../consts";
import { useAccent } from "../../hooks";
import { formatDuration, hasPassedExpiration } from "../../utils/datetime";
import {
  StyledDurationBadge,
  TitleTypography
} from "../CarouselCard/CarouselCard.styles";
import { CircularProgress } from "../CircularProgress";
import { TruncatedText } from "../TruncatedText";


export function CarouselCardMobile({
  categories,
  coverPublicURL,
  courseId,
  duration,
  disabled,
  ecmRegistration,
  ecmType,
  endDate,
  editionNumber,
  enrollType,
  expirationDate,
  grandParentId,
  id,
  isStandAlone,
  ecm_specialization,
  iconStatus,
  isMandatory,
  isTest,
  isSurvey,
  learningObjectType,
  learningObjectTypology,
  madeAttempts,
  open,
  parentId,
  percentageOfCompletion,
  rootId,
  setOpen,
  setOpenModalEcm,
  startDate,
  status,
  title,
  canAccessNow,
  isLinkRelative,
  link,
  credits
}: CarouselCardMobileProps) {
  const theme = useTheme();
  const isExpired = hasPassedExpiration(expirationDate);
  const accentColor = useAccent({ isExpired, isMandatory, status });
  const { t } = useTranslation();
  const durationNumber = parseInt(`${duration}`);

  const messageStatusBox = (isExpired && expirationDate && status !== "C" && isMandatory )
    ? `${ t("expired") }`
    : undefined;

  const categoryTags = categories.slice(0,2)
    .slice(0, 2)
    .concat(categories.length > 2 ? [`+${categories.length - 2}`] : []);

  return (
    <StyledSwipeableDrawer
      id={ `drawer-${title}` }
      anchor="bottom"
      onClose={ () => setOpen({
        label: "",
        openId: "",
        state: false
      }) }
      onOpen={ () => {} }
      open={ open.state || title === open.label }
      variant={ "temporary" }
      sx={ {
        zIndex: 1000000 //To keep on top of external widgets
      } }
    >
      <CarouselSlideBox>
        <CarouselCardGrid
          container
          accent={ accentColor }
          className={ "carosel-card--active" }
          component="section"
          direction="column-reverse"
          $coverPublicURL={ coverPublicURL }
          padding="0 1.4rem 1.4rem"
        >
          <Grid
            item
            xs={ 6 }
            container
            direction={ "column" }
            justifyContent={ "end" }
          >

            <Box sx={ {
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              position: "absolute",
              top: theme.spacing(3),
              right: theme.spacing(3),
              gap: theme.spacing(2)
            } }>
              {
                duration && !isNaN(durationNumber) && durationNumber > 0 ?
                  <StyledDurationBadge
                    top= { theme.spacing(3) }
                    padding={ theme.spacing(0.56, 1) }
                    right= { theme.spacing(1) }
                    $absolute={ false }
                    $completed={ status === STATUS_LO.C }>
                    <Icon icon={ "Icons_time" } size={ 24 }/>
                    { duration && formatDuration(duration, t) }
                  </StyledDurationBadge> : null
              }

              {
                (
                  percentageOfCompletion
                  && (status === STATUS_LO.P || status === STATUS_LO.C)
                  && (learningObjectTypology === LEVEL.PATH || learningObjectTypology === LEVEL.COURSE)
                )
                  ? (
                    <CircularProgress percentageOfCompletion={ percentageOfCompletion } />
                  ) : null
              }
            </Box>

            { (status === "C" || isMandatory ) &&
              <StatusBox
                message={ messageStatusBox }
                accent={ accentColor }
                icon={ iconStatus }
              />
            }

            <CardType
              ecmType={ ecmType }
              ecm_specialization={ ecm_specialization }
              fontSize="0.875rem"
              lineHeight={ 3 / 2 }
              textTransform={ "uppercase" }
              fontWeight={ 700 }
              isSurvey={ Boolean(isSurvey) }
              isTest={ Boolean(isTest) }
              learningObjectTypology={ learningObjectTypology }
              learningObjectType={ learningObjectType }
            />
            <TitleTypography
              $clamp={ 1 }
              fontSize="1.5rem"
              lineHeight={ 5 / 4 }
              marginBottom={ "8px" }
            >
              <TruncatedText text={ title } />
            </TitleTypography>
            <CardCategories
              categories={ categoryTags }
              marginBottom={ "8px" }/>
            <Stack gap={ 1 }>
              <CardSignature
                startDate={ startDate }
                endDate={ endDate }
                editionNumber={ editionNumber }
                alignItems="center"
                gap={ 2 }
                expirationDate={ expirationDate }
                learningObjectTypology={ learningObjectTypology }
                canAccessNow={ canAccessNow }
              />
              <CardCta
                courseId={ courseId }
                disabled={ disabled }
                ecm_specialization={ ecm_specialization }
                ecmRegistration={ ecmRegistration }
                enrollType={ enrollType }
                gap={ theme.spacing(2) }
                grandParentId={ grandParentId }
                id={ id }
                isCard={ true }
                isStandAlone={ isStandAlone }
                isSurvey={ isSurvey }
                isTest={ isTest }
                learningObjectType={ learningObjectType }
                learningObjectTypology={ learningObjectTypology }
                madeAttempts={ madeAttempts }
                parentId={ parentId }
                marginTop={ theme.spacing(1) }
                percentageOfCompletion={ percentageOfCompletion }
                rootId={ rootId }
                setOpenModalEcm={ setOpenModalEcm }
                status={ status }
                title={ title }
                credits={ credits }
                vertical
                canAccessNow={ canAccessNow }
                isLinkRelative={ isLinkRelative }
                link={ link }
                showDetailsButton={ true }
                ctaPosition={ "split" }
              />
            </Stack>

            { /* FIXME: questo link non funziona (è nascosto ma visibile da screen reader) mancano parametri corretti e state */ }
            <LinkWithInteractableOverlay
              $disableLink={ disabled }
              $hideCta
              href={ `/esplora/dettaglio/${id}` }
              tabIndex={ 0 }
              role={ "link" }
            >
              { title }
            </LinkWithInteractableOverlay>
          </Grid>
        </CarouselCardGrid>
      </CarouselSlideBox>
    </StyledSwipeableDrawer>
  );
}

function StatusBox({
  accent,
  icon,
  message,
  ...props
} : StatusBoxProps) {
  const theme = useTheme();

  return (
    <StyledStatusBox
      $accent={ accent }
      width={ message ? "fit-content" : "2.5rem" }
      { ...props }

    >
      <Icon
        icon={ icon }
        color={ theme.customColors.systemSecondary05 }
        size={ "24px" }
      />
      {
        (message) ? (
          <Typography
            component="span"
            fontSize={  "0.90rem" }
            fontWeight="500"
            color={ theme.customColors.textPrimaryAlternative }
          >
            { message }
          </Typography>
        ): null
      }
    </StyledStatusBox>
  );
}
